import { CellChangeItem, GenerateExcelPreviewResponse } from '@/api'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { Message } from 'primereact/message'
import { ProgressBar } from 'primereact/progressbar'

interface ExcelWritePreviewDialogProps {
  visible: boolean
  isLoading: boolean
  lastLog?: string
  error?: string | null
  preview?: GenerateExcelPreviewResponse | null
  onCancel(): void
  onConfirm(): void
}

const show = (v: unknown): string => (v === null || v === undefined || v === '' ? '（空）' : String(v))

const ExcelWritePreviewDialog = ({
  visible,
  isLoading,
  lastLog,
  error,
  preview,
  onCancel,
  onConfirm,
}: ExcelWritePreviewDialogProps) => {
  const changes = preview?.changes ?? []
  const written = changes.filter((c) => c.new !== null && c.new !== undefined).length
  const cleared = changes.length - written

  return (
    <Dialog
      header="Excelファイルに保存する内容の確認"
      visible={visible}
      style={{ width: '70vw' }}
      onHide={onCancel}
      maximizable
      footer={
        <div className="flex justify-content-end gap-2">
          <Button label="キャンセル" severity="secondary" onClick={onCancel} />
          <Button
            label="この内容で保存"
            icon="pi pi-file-excel"
            severity="success"
            onClick={onConfirm}
            disabled={isLoading || Boolean(error) || !preview}
          />
        </div>
      }
    >
      {isLoading && (
        <div className="mb-3">
          <ProgressBar mode="indeterminate" />
          <small className="block mt-2 text-600">{lastLog ?? '書き換えるセルを調べています...'}</small>
        </div>
      )}
      {error && <Message severity="error" text={error} className="mb-3 w-full justify-content-start" />}
      {preview && (
        <>
          <p className="mt-0">
            成形の行で <b>{changes.length}</b> セルが変わります（書き込み {written}・消去 {cleared}）。
            保存の前に、今の OneDrive のファイルをサーバーにバックアップします。
          </p>
          <ul className="text-sm text-600 mt-0">
            <li>書き換えるのは、計画の対象の品目の成形行の、計画期間の列だけです。</li>
            <li>
              外注・対象外の品目（{preview.keptSkus.length} 品目）の行、成形一覧に無い機械の行、ロック中の日は書き換えません。
            </li>
          </ul>
          {preview.skipped.length > 0 && (
            <Message
              severity="warn"
              className="mb-3 w-full justify-content-start"
              text={`「作らない」品目の計画 ${preview.skipped.length} 件は書き込みません（「作るかどうかの確認」で「生産する」にすると書き込めます）: ${Array.from(
                new Set(preview.skipped.map((s) => s.sku)),
              ).join(', ')}`}
            />
          )}
          <DataTable value={changes} size="small" paginator rows={15} emptyMessage="変わるセルはありません" sortMode="multiple">
            <Column field="sheet" header="シート" sortable />
            <Column field="cell" header="セル" />
            <Column field="sku" header="品目" sortable />
            <Column field="machine" header="機械" sortable />
            <Column field="date" header="日付" sortable />
            <Column
              header="旧値 → 新値"
              body={(c: CellChangeItem) => (
                <span>
                  {show(c.old)} → <b>{show(c.new)}</b>
                </span>
              )}
            />
          </DataTable>
        </>
      )}
    </Dialog>
  )
}

export default ExcelWritePreviewDialog
